import type { PartialDeep } from 'type-fest';
import type { ListingDetails } from 'app/types/listingDetails.type';
import {
  listingUtils_bathDisplay,
  listingUtils_bedDisplay,
  listingUtils_getEffectivePropertyType,
  listingUtils_summaryPriceText,
} from 'app/shared/utils/listingUtils';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

type AvailabilitySource = { availabilityDate?: string } | undefined;

function getHaloAvailabilityDisplay(listing: { active?: boolean } | undefined, floorplan: AvailabilitySource): string {
  if (listing?.active === false) {
    return 'No longer available';
  }
  const date = floorplan?.availabilityDate;
  if (!date) {
    return '';
  }
  const [, monthStr, dayStr] = date.split('-');
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  if (!Number.isFinite(month) || !Number.isFinite(day) || month < 1 || month > 12) {
    return '';
  }
  return `Available ${MONTH_NAMES[month - 1]} ${day}`;
}

function getHaloPropertyTypeLabel(rawPropertyType: string | undefined, hasAnyRange: boolean): string {
  if (!rawPropertyType) {
    return '';
  }
  const effective = listingUtils_getEffectivePropertyType(rawPropertyType);
  if (!effective) {
    return '';
  }
  if (effective === 'Apartment building') {
    return hasAnyRange ? 'apartments' : 'apartment';
  }
  return effective.toLowerCase();
}

// Reads ~10 fields via optional chaining, so it accepts a recursive partial
// rather than a fully-populated ListingDetails — callers (including tests) can
// pass realistic partial shapes without casting.
export function getListingSummaryDisplay(currentListing: PartialDeep<ListingDetails, { recurseIntoArrays: true }>) {
  const { active, isAllowedToRetain } = currentListing;
  const shouldHideDetails = active === false && isAllowedToRetain === false;

  const priceSummary = currentListing.pricing?.summary;
  const minMaxPriceBeds = currentListing.listingMinMaxPriceBeds;

  const priceObj =
    priceSummary?.priceLow || priceSummary?.priceHigh
      ? priceSummary
      : { priceLow: minMaxPriceBeds?.minPrice, priceHigh: minMaxPriceBeds?.maxPrice };

  const priceDisplay = listingUtils_summaryPriceText(priceObj);
  const showPrice = priceDisplay && priceDisplay !== 'Call';

  // listingMinMaxPriceBeds is a building-level aggregate that can include bed configs
  // with no available units, causing a mismatch with the bedroom tabs in UnitsModule
  // (which are derived from floorplans). Prefer floorplans as the source of truth,
  // then pricing.summary (like legacy HDP), then listingMinMaxPriceBeds as last resort.
  const floorplans = currentListing.floorplans;
  let minBeds: number | undefined;
  let maxBeds: number | undefined;

  if (floorplans && floorplans.length > 0) {
    const bedCounts = floorplans.map((fp) => fp?.beds).filter((b): b is number => b != null);
    if (bedCounts.length > 0) {
      minBeds = Math.min(...bedCounts);
      maxBeds = Math.max(...bedCounts);
    }
  } else if (priceSummary?.bedsLow != null || priceSummary?.bedsHigh != null) {
    minBeds = priceSummary.bedsLow;
    maxBeds = priceSummary.bedsHigh;
  } else {
    minBeds = minMaxPriceBeds?.minBeds;
    maxBeds = minMaxPriceBeds?.maxBeds;
  }

  const bedRangeDisplay = listingUtils_bedDisplay(minBeds, maxBeds, undefined, '-');
  const showBedRange = bedRangeDisplay && bedRangeDisplay !== '';

  let minBaths: number | undefined;
  let maxBaths: number | undefined;

  if (floorplans && floorplans.length > 0) {
    const bathCounts = floorplans.map((fp) => fp?.baths).filter((b): b is number => b != null);
    if (bathCounts.length > 0) {
      minBaths = Math.min(...bathCounts);
      maxBaths = Math.max(...bathCounts);
    }
  } else if (priceSummary?.bathsLow != null || priceSummary?.bathsHigh != null) {
    minBaths = priceSummary.bathsLow;
    maxBaths = priceSummary.bathsHigh;
  }

  const bathRangeDisplay = listingUtils_bathDisplay(minBaths, maxBaths, 'long', '-');
  const showBathRange = bathRangeDisplay && bathRangeDisplay !== '';

  let minSqft: number | undefined;
  let maxSqft: number | undefined;

  if (floorplans && floorplans.length > 0) {
    const sqftValues = floorplans.map((fp) => fp?.sqft).filter((s): s is number => typeof s === 'number' && s > 0);
    if (sqftValues.length > 0) {
      minSqft = Math.min(...sqftValues);
      maxSqft = Math.max(...sqftValues);
    }
  } else if (priceSummary?.sqFtLow != null || priceSummary?.sqFtHigh != null) {
    minSqft = priceSummary.sqFtLow;
    maxSqft = priceSummary.sqFtHigh;
  }

  let sqftDisplay = '';
  if (typeof minSqft === 'number' && minSqft > 0 && typeof maxSqft === 'number' && maxSqft > 0) {
    const lo = Math.min(minSqft, maxSqft);
    const hi = Math.max(minSqft, maxSqft);
    sqftDisplay = lo === hi ? `${lo} sqft` : `${lo} - ${hi} sqft`;
  } else if (typeof minSqft === 'number' && minSqft > 0) {
    sqftDisplay = `${minSqft} sqft`;
  } else if (typeof maxSqft === 'number' && maxSqft > 0) {
    sqftDisplay = `${maxSqft} sqft`;
  }
  const showSqft = sqftDisplay !== '';

  const unitCount = currentListing.modelTypeUnitCount;
  const isRoomForRent = currentListing.listingType === 'room';
  const isSublet = currentListing.listingType === 'sublet';
  const isSingleUnit = currentListing.isUnit === true;
  const showUnits = unitCount && unitCount > 0 && !isRoomForRent && !isSublet && !isSingleUnit;

  const roomSqFt = currentListing.details?.roomForRent?.roomDetails?.roomSqFt;
  const roommatesCount = currentListing.details?.roomForRent?.roommateDetails?.roommatesCount;
  const showRoomSqFt = isRoomForRent && Boolean(roomSqFt);
  const showHousemates = isRoomForRent && Boolean(roommatesCount);
  const housematesLabel = roommatesCount === '1' ? 'housemate' : 'housemates';

  const address = currentListing.address;
  const addressDisplay = address?.hideStreet
    ? `(Undisclosed Address), ${address.city}, ${address.state} ${address.zip}`
    : `${address?.street}, ${address?.city}, ${address?.state} ${address?.zip}`;

  const websiteUrl = currentListing.details?.webSite;
  const websiteLinkText = currentListing.displayName
    ? `Visit ${currentListing.displayName} website`
    : 'Visit property website';

  const availabilityDisplay = getHaloAvailabilityDisplay(currentListing, floorplans?.[0]);
  const showAvailability = Boolean(availabilityDisplay);

  const bedsAreRange = minBeds != null && maxBeds != null && minBeds !== maxBeds;
  const bathsAreRange = minBaths != null && maxBaths != null && minBaths !== maxBaths;
  const sqftIsRange =
    typeof minSqft === 'number' && typeof maxSqft === 'number' && minSqft > 0 && maxSqft > 0 && minSqft !== maxSqft;
  const hasAnyRange = bedsAreRange || bathsAreRange || sqftIsRange;

  const propertyTypeDisplay = getHaloPropertyTypeLabel(currentListing.propertyType, hasAnyRange);
  const showPropertyType = propertyTypeDisplay !== '';

  const hasTotalMonthlyPrice = Boolean(currentListing.hasTotalMonthlyPrice);
  const costsExplainerText = !showPrice
    ? ''
    : hasTotalMonthlyPrice
      ? 'Now includes required monthly fees in total price'
      : "Doesn't include deposits and fees";
  const showCostsExplainer = costsExplainerText !== '';

  return {
    shouldHideDetails,
    priceDisplay,
    showPrice,
    bedRangeDisplay,
    showBedRange,
    bathRangeDisplay,
    showBathRange,
    sqftDisplay,
    showSqft,
    unitCount,
    showUnits,
    roomSqFt,
    showRoomSqFt,
    roommatesCount,
    showHousemates,
    housematesLabel,
    addressDisplay,
    websiteUrl,
    websiteLinkText,
    availabilityDisplay,
    showAvailability,
    propertyTypeDisplay,
    showPropertyType,
    costsExplainerText,
    showCostsExplainer,
  };
}
